'use client';

import ProfileCard, { ProfileCardProps } from '@dvd/components/ProfileCard';
import TextLink from '@dvd/components/TextLink';
import { CtaProps } from '@types';
import { styled } from 'styled-components';
import * as Styled from '@lib/styles';

// Extend ProfileCard props with additional Profile-specific props
type ProfileProps = ProfileCardProps & {
  /*
   * The back link.
   */
  breadcrumb: CtaProps;
};

const ProfileContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: ${({ theme }) => theme.spacing[1]};

  p {
    margin-block-end: 0;
  }

  .Profile__breadcrumb {
    color: ${({ theme }) => theme.colors.grey500};

    // Override the default link styling of an → arrow after the text
    &::after {
      content: '';
    }

    // Add an ← arrow before the text and animate it when hovered
    // TODO: Get with design if this will be a breadcrumb needed in DVD further down the line.
    &::before {
      content: '←';
      display: inline-block;
      padding-inline-end: ${({ theme }) => theme.spacing['0.25']};
      transition: transform var(--global-motion);
    }

    &:hover,
    &:active {
      color: var(--global-hover-color);
      text-decoration: underline currentcolor;

      &::before {
        transform: translateX(-${({ theme }) => theme.spacing['0.25']});
      }
    }
  }
`;

/*
 * The profile module.
 *
 * This module is used to display a profile card with a breadcrumb link at the top.
 * When used on the integrations page, the breadcrumb link is to the integrations page.
 * The text should be 'Browse all integrations' and the href should be '/integrations'.
 *
 * Rules:
 * - This module should never be used in a section only a section split variant.
 *
 */
const Profile = ({ breadcrumb, ...profileCardProps }: ProfileProps) => {
  return (
    <ProfileContainer>
      <Styled.BodyXs>
        <TextLink cta={breadcrumb} className="Profile__breadcrumb" />
      </Styled.BodyXs>
      <ProfileCard {...profileCardProps} />
    </ProfileContainer>
  );
};

export default Profile;
